import { useQuery } from "@tanstack/react-query";
import client from "@phrmpc/infrastructure/phrmpc/axios";
import { resolveAttachmentContent } from "../utils/resolveAttachmentContent";

type UseTransmittalAttachmentContentParams = {
  transmittalDocumentId: string;
  fileId: string;
  fileName: string;
  enabled?: boolean;
};

export function useTransmittalAttachmentContent({
  transmittalDocumentId,
  fileId,
  fileName,
  enabled = true,
}: UseTransmittalAttachmentContentParams) {
  return useQuery({
    queryKey: ["transmittal-attachment-content", transmittalDocumentId, fileId],
    queryFn: async () => {
      const response = await client.get(
        `/transmittal/transmittal-documents/${transmittalDocumentId}/files/${fileId}/content`,
        { responseType: "blob" },
      );
      const resolved = await resolveAttachmentContent(response, fileName);
      return URL.createObjectURL(resolved.blob);
    },
    enabled: enabled && Boolean(transmittalDocumentId) && Boolean(fileId),
    staleTime: Infinity,
  });
}

// Keyed by fileId - mirrors the hook's own `staleTime: Infinity`/queryKey shape, so a preview
// opened twice for the same file doesn't refetch the blob or mint a second object URL.
const transmittalAttachmentUrlCache = new Map<string, Promise<string>>();

/**
 * Plain-function counterpart to `useTransmittalAttachmentContent`, for use as a `ControlledFileUploader`
 * `getFileUrl` callback - those are called lazily on preview/download click, not rendered, so they
 * can't call a React Query hook.
 */
export function resolveTransmittalAttachmentUrl(
  transmittalDocumentId: string,
  fileId: string,
  fileName: string,
): Promise<string> {
  const cached = transmittalAttachmentUrlCache.get(fileId);
  if (cached) return cached;

  const promise = client
    .get(`/transmittal/transmittal-documents/${transmittalDocumentId}/files/${fileId}/content`, {
      responseType: "blob",
    })
    .then(async (response) => {
      const resolved = await resolveAttachmentContent(response, fileName);
      return URL.createObjectURL(resolved.blob);
    });

  transmittalAttachmentUrlCache.set(fileId, promise);
  promise.catch(() => transmittalAttachmentUrlCache.delete(fileId));
  return promise;
}
